<template>
  <div>
    <!-- Mobile Overlay -->
    <div 
      v-if="isMobileMenuOpen" 
      @click="closeMobileMenu"
      class="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
    ></div>

    <!-- Sidebar -->
    <aside
      :class="[
        'fixed left-0 top-16 bottom-0 bg-white border-r border-gray-200 shadow-lg transition-all duration-300 z-40',
        'lg:translate-x-0',
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        isCollapsed ? 'w-16' : 'w-64'
      ]"
    >
      <!-- Navigation Items -->
      <nav class="h-full flex flex-col py-4 overflow-y-auto">
        <div class="px-2 flex-1">
          <!-- Dashboard -->
          <RouterLink
            to="/dashboard"
            @click="handleNavClick"
            :class="[
              'flex items-center px-3 py-2.5 mb-1 text-sm font-medium rounded-lg transition-colors',
              isActiveNav('/dashboard') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
            ]"
            :title="isCollapsed ? 'Dashboard' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span v-if="!isCollapsed" class="ml-3">Dashboard</span>
          </RouterLink>

          <!-- AI Analysis -->
          <RouterLink
            to="/recommendations"
            @click="handleNavClick"
            :class="[
              'flex items-center px-3 py-2.5 mb-1 text-sm font-medium rounded-lg transition-colors',
              isActiveNav('/recommendations') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
            ]"
            :title="isCollapsed ? 'AI Analysis' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <span v-if="!isCollapsed" class="ml-3">AI Analysis</span>
          </RouterLink>

          <!-- AI Chat -->
          <RouterLink
            to="/chat"
            @click="handleNavClick"
            :class="[
              'flex items-center px-3 py-2.5 mb-1 text-sm font-medium rounded-lg transition-colors',
              isActiveNav('/chat') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
            ]"
            :title="isCollapsed ? 'AI Chat' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span v-if="!isCollapsed" class="ml-3">AI Chat</span>
          </RouterLink>

          <!-- API & MCP -->
          <RouterLink
            to="/mcp"
            @click="handleNavClick"
            :class="[
              'flex items-center px-3 py-2.5 mb-1 text-sm font-medium rounded-lg transition-colors',
              isActiveNav('/mcp') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
            ]"
            :title="isCollapsed ? 'API & MCP' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span v-if="!isCollapsed" class="ml-3">API & MCP</span>
          </RouterLink>

          <!-- Portfolio -->
          <RouterLink
            to="/portfolio"
            @click="handleNavClick"
            :class="[
              'flex items-center px-3 py-2.5 mb-1 text-sm font-medium rounded-lg transition-colors',
              isActiveNav('/portfolio') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
            ]"
            :title="isCollapsed ? 'Portfolio' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <span v-if="!isCollapsed" class="ml-3">Portfolio</span>
          </RouterLink>

          <!-- Analytics -->
          <RouterLink
            to="/analytics"
            @click="handleNavClick"
            :class="[
              'flex items-center px-3 py-2.5 mb-1 text-sm font-medium rounded-lg transition-colors',
              isActiveNav('/analytics') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
            ]"
            :title="isCollapsed ? 'Analytics' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span v-if="!isCollapsed" class="ml-3">Analytics</span>
          </RouterLink>

          <!-- Onboarding -->
          <RouterLink
            to="/onboarding"
            @click="handleNavClick"
            :class="[
              'flex items-center px-3 py-2.5 mb-1 text-sm font-medium rounded-lg transition-colors',
              isActiveNav('/onboarding') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
            ]"
            :title="isCollapsed ? 'Onboarding' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <span v-if="!isCollapsed" class="ml-3">Onboarding</span>
          </RouterLink>

          <!-- Price Alerts -->
          <RouterLink
            to="/alerts"
            @click="handleNavClick"
            :class="[
              'flex items-center px-3 py-2.5 mb-1 text-sm font-medium rounded-lg transition-colors',
              isActiveNav('/alerts') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
            ]"
            :title="isCollapsed ? 'Price Alerts' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span v-if="!isCollapsed" class="ml-3">Price Alerts</span>
          </RouterLink>

          <!-- Stock Search -->
          <RouterLink
            to="/stocks"
            @click="handleNavClick"
            :class="[
              'flex items-center px-3 py-2.5 mb-1 text-sm font-medium rounded-lg transition-colors',
              isActiveNav('/stocks') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
            ]"
            :title="isCollapsed ? 'Stock Search' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span v-if="!isCollapsed" class="ml-3">Stock Search</span>
          </RouterLink>

          <!-- Watchlist -->
          <RouterLink
            to="/watchlist"
            @click="handleNavClick"
            :class="[
              'flex items-center px-3 py-2.5 mb-1 text-sm font-medium rounded-lg transition-colors',
              isActiveNav('/watchlist') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
            ]"
            :title="isCollapsed ? 'Watchlist' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
            <span v-if="!isCollapsed" class="ml-3">Watchlist</span>
          </RouterLink>

          <!-- Premium -->
          <RouterLink
            to="/pricing"
            @click="handleNavClick"
            :class="[
              'flex items-center px-3 py-2.5 mb-1 text-sm font-medium rounded-lg transition-colors',
              isActiveNav('/pricing') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
            ]"
            :title="isCollapsed ? 'Premium' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            <span v-if="!isCollapsed" class="ml-3 flex items-center">
              Premium
              <span class="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-600">⭐</span>
            </span>
          </RouterLink>

          <!-- Settings (Authenticated Users) -->
          <RouterLink
            v-if="authStore.user"
            to="/settings"
            @click="handleNavClick"
            :class="[
              'flex items-center px-3 py-2.5 mb-1 text-sm font-medium rounded-lg transition-colors border-t border-gray-200 mt-4 pt-4',
              route.path === '/settings' || route.path.startsWith('/settings/')
                ? 'bg-blue-50 text-blue-600' 
                : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
            ]"
            :title="isCollapsed ? 'Settings' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span v-if="!isCollapsed" class="ml-3">Settings</span>
          </RouterLink>
        </div>

        <!-- Collapse Toggle - Desktop Only -->
        <div class="mt-auto pt-4 px-2 border-t border-gray-200 hidden lg:block">
          <button
            @click="toggleCollapse"
            :class="[
              'flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-colors',
              'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
            ]"
            :title="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
          >
            <svg 
              class="w-5 h-5 flex-shrink-0 transition-transform"
              :class="isCollapsed ? 'rotate-180' : ''"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
            <span v-if="!isCollapsed" class="ml-3">Collapse</span>
          </button>
        </div>
      </nav>
    </aside>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useSidebarStore } from '../stores/sidebar'

const route = useRoute()
const authStore = useAuthStore()
const sidebarStore = useSidebarStore()

const isCollapsed = computed(() => sidebarStore.isCollapsed)
const isMobileMenuOpen = computed(() => sidebarStore.isMobileMenuOpen)

const isActiveNav = (path: string) => {
  return route.path === path || route.path.startsWith(path + '/')
}

const toggleCollapse = () => {
  sidebarStore.toggleCollapse()
}

const closeMobileMenu = () => {
  sidebarStore.closeMobileMenu()
}

const handleNavClick = () => {
  closeMobileMenu()
}
</script>
